// Copyright Epic Games, Inc. All Rights Reserved.

import {
  ForkEffect,
  put,
  delay,
  take,
  takeEvery,
  cancel,
  TakeEffect,
  CancelEffect,
  SelectEffect,
  CallEffect,
  PutEffect,
} from "@redux-saga/core/effects";
import { select, call, fork } from "typed-redux-saga";
import {
  CANCEL_INSTALLATION,
  setInstallationCancelling,
  setInstallationErrorCode,
  setInstallationProgress,
  START_INSTALLATION_PULLING,
  STOP_INSTALLATION_PULLING,
} from "./actions";
import {
  cancelInstallation,
  getInstallationProgress,
  IInstallationGroupProgressMessage,
} from "../../services/EOSHelperAPI";
import { selectInstallationGroupId } from "./selectors";
import { StatusCodeError } from "../../errors/StatusCodeError";
import {
  InstallationFailedFetchErrorCode,
  InstallationUnexpectedErrorErrorCode,
} from "../../errors/installation.errorCodes";
import {
  patchErrorCodeLocalization,
  patchLocalizationMessages,
} from "../localization/actions";
import { selectMainServiceAccessToken } from "../shared/selectors";

const mapErrorToPutErrorCode = (error: Error): PutEffect => {
  if (error instanceof StatusCodeError) {
    return put(
      setInstallationErrorCode(
        InstallationFailedFetchErrorCode + "-" + String(error.statusCode)
      )
    );
    // Means that happened some network error. e.g. net::ERR_CONNECTION_REFUSED
    // No way to check the actual error code so relying on the error message.
  } else if (error.message.includes("Failed to fetch")) {
    return put(setInstallationErrorCode(InstallationFailedFetchErrorCode));
  }
  return put(setInstallationErrorCode(InstallationUnexpectedErrorErrorCode));
};

export function* installationPullingAsync(): Generator<
  SelectEffect | CallEffect | PutEffect
> {
  let retryLimitFor404Code = 3;

  while (true) {
    try {
      const groupId: string = yield* select(selectInstallationGroupId);
      const accessToken: string = yield* select(selectMainServiceAccessToken);

      const installationProgress: IInstallationGroupProgressMessage = yield* call(
        getInstallationProgress,
        groupId,
        accessToken
      );

      console.log(
        `Got the installation progress for installation group ${groupId}`
      );

      const { currentTaskProgress } = installationProgress;

      // Received error code from Install Helper.
      if ("errorCode" in currentTaskProgress && currentTaskProgress.errorCode) {
        console.error("Received an errorCode from EOSH!");

        yield put(
          patchErrorCodeLocalization({
            errorCode: currentTaskProgress.errorCode,
            title: currentTaskProgress.errorTitle,
            description: currentTaskProgress.errorDescription,
          })
        );
        yield put(setInstallationErrorCode(currentTaskProgress.errorCode));
        return;
      }

      if ("alertCode" in currentTaskProgress && currentTaskProgress.alertCode) {
        yield put(
          patchErrorCodeLocalization({
            errorCode: currentTaskProgress.alertCode,
            title: "",
            description: currentTaskProgress.alertDescription,
          })
        );
      }

      yield put(
        patchLocalizationMessages({
          "installation.title": currentTaskProgress.title,
        })
      );

      console.log("Put installation.title localization message!");

      yield put(setInstallationProgress(installationProgress));

      console.log("Updated installation progress!");

      if (
        installationProgress.numInstalled ===
        installationProgress.numInstallations
      ) {
        console.log("Installation ended, closing the window!");

        window.close();
        return;
      }

      yield delay(1000);
    } catch (error) {
      console.error("Failed installation progress pull with an error. ", error);

      if (
        error instanceof StatusCodeError &&
        error.statusCode === 404 &&
        retryLimitFor404Code !== 0
      ) {
        retryLimitFor404Code--;

        yield delay(1000);
      } else {
        yield mapErrorToPutErrorCode(error);
        return;
      }
    }
  }
}

export function* startInstallationPullingAsync(): Generator<
  ForkEffect | TakeEffect | CancelEffect
> {
  const pullingTask = yield* fork(installationPullingAsync);

  yield take(STOP_INSTALLATION_PULLING);
  yield cancel(pullingTask);
}

export function* cancelInstallationAsync(): Generator<
  PutEffect | SelectEffect | CallEffect
> {
  try {
    yield put(setInstallationCancelling(true));

    const groupId: string = yield* select(selectInstallationGroupId);
    const accessToken: string = yield* select(selectMainServiceAccessToken);

    yield* call(cancelInstallation, groupId, accessToken);

    console.log(`Cancelled installation group ${groupId}.`);
  } catch (error) {
    // There is nothing we can do for user if we can not perform a cancel request
    // so let's just close the window.
    console.error("Failed to cancel installation due to an error. ", error);
  } finally {
    console.log("Closing the window!");
    window.close();
  }
}

export function* watchInstallation(): Generator<ForkEffect> {
  yield takeEvery(START_INSTALLATION_PULLING, startInstallationPullingAsync);
  yield takeEvery(CANCEL_INSTALLATION, cancelInstallationAsync);
}
